// EditCustomer.jsx
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const ConfirmModal = ({ show, title, message, onConfirm, onCancel }) => {
    if (!show) return null;
    return (
        <>
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050 }} onClick={onCancel} />
            <div role="dialog" aria-modal="true" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1055, width: "100%", maxWidth: 420, background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.18)", padding: "28px 28px 22px" }}>
                <div className="d-flex align-items-center gap-3 mb-3">
                    <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff0f0" }}>
                        <i className="bi bi-trash3" style={{ fontSize: 20, color: "#dc3545" }} />
                    </div>
                    <h6 className="mb-0 fw-bold" style={{ fontSize: 16 }}>{title}</h6>
                </div>
                <p className="text-muted mb-4" style={{ fontSize: 14, lineHeight: 1.6 }}>{message}</p>
                <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn-sm btn-danger px-4" style={{ borderRadius: 8 }} onClick={onConfirm}>Delete</button>
                    <button className="btn btn-sm btn-outline-secondary px-4" style={{ borderRadius: 8 }} onClick={onCancel}>Cancel</button>
                </div>
            </div>
        </>
    );
};

const Section = ({ title, children }) => {
    const [open, setOpen] = useState(true);
    return (
        <div className="mb-3 border">
            <div
                className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
                onClick={() => setOpen(!open)}
            >
                <span>{title}</span>
                <span>{open ? "▾" : "▸"}</span>
            </div>
            {open && <div className="row g-3 p-3">{children}</div>}
        </div>
    );
};

const INDUSTRIES = ["IT", "Construction", "Manufacturing", "Consulting", "Marketing", "Healthcare", "Finance", "Education", "Other"];
const CUSTOMER_TYPES = ["Individual", "Company", "Government", "NGO", "Other"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
const PAYMENT_TERMS = ["Net 15", "Net 30", "Net 45", "Due on receipt"];
const STATUSES = ["Active", "Inactive", "Blacklisted"];
const ADDRESS_TYPES = ["Billing", "Shipping", "Home", "Work", "Other"];

const emptyAddress = () => ({ addressType: "Billing", addressLine1: "", addressLine2: "", city: "", state: "", country: "", postalCode: "" });

export default function EditCustomer() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({});
    const [contacts, setContacts] = useState([]);
    const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", designation: "", department: "", addresses: [], notes: "", primary: false });
    const [editContactIdx, setEditContactIdx] = useState(null);
    const [tagInput, setTagInput] = useState("");
    const [activeTab, setActiveTab] = useState("basic");
    const [activeSubTab, setActiveSubTab] = useState("systemNotes");
    const [systemNotes, setSystemNotes] = useState([]);
    const [navbarColor, setNavbarColor] = useState("#080235");
    const [savingContact, setSavingContact] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ show: false, title: "", message: "", onConfirm: null });

    const token = localStorage.getItem("token");

    const closeConfirm = () => setConfirmModal({ show: false, title: "", message: "", onConfirm: null });

    const [addressModal, setAddressModal] = useState(false);
    const [editAddressIdx, setEditAddressIdx] = useState(null); // null = new
    const [addressDraft, setAddressDraft] = useState(emptyAddress());

    // Bootstrap loader
    useEffect(() => {
        const bootstrapLink = document.createElement("link");
        bootstrapLink.rel = "stylesheet";
        bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
        document.head.appendChild(bootstrapLink);
        const iconsLink = document.createElement("link");
        iconsLink.rel = "stylesheet";
        iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
        document.head.appendChild(iconsLink);
        return () => { bootstrapLink.remove(); iconsLink.remove(); };
    }, []);

    // Auth check
    useEffect(() => {
        const roles = JSON.parse(localStorage.getItem("roles") || "[]");
        if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
        if (!roles.includes("ROLE_ADMIN") && !roles.includes("ROLE_CUSTOMER_MANAGER")) {
            toast.error("Unauthorized."); navigate("/"); return;
        }
    }, [navigate, token]);

    // Navbar color
    useEffect(() => {
        const userId = localStorage.getItem("userId");
        if (!token || !userId) return;
        fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((d) => { if (d?.navbarColor) setNavbarColor(d.navbarColor); })
            .catch(() => { });
    }, [token]);

    // Load customer
    useEffect(() => {
        if (!id) return;
        fetch(`${API_BASE_URL}/api/customers/${id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((data) => setFormData({ tags: [], ...data }))
            .catch(() => toast.error("Failed to load customer."));
    }, [id, token]);

    // Load contacts
    const fetchContacts = () => {
        fetch(`${API_BASE_URL}/api/customers/${id}/contacts`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : [])
            .then((data) => setContacts(data))
            .catch(() => setContacts([]));
    };
    useEffect(() => { if (id) fetchContacts(); }, [id, token]);

    // Load system notes
    useEffect(() => {
        if (!id) return;
        fetch(`${API_BASE_URL}/api/audit/customer/${id}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.ok ? r.json() : [])
            .then((data) => setSystemNotes(data))
            .catch(() => setSystemNotes([]));
    }, [id, token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleAddressChange = (idx, field, value) => {
        setFormData((prev) => {
            const updated = [...(prev.addresses || [])];
            updated[idx] = { ...updated[idx], [field]: value };
            return { ...prev, addresses: updated };
        });
    };
    const addAddress = () => setFormData((prev) => ({ ...prev, addresses: [...(prev.addresses || []), emptyAddress()] }));
    const removeAddress = (idx) => setFormData((prev) => ({ ...prev, addresses: (prev.addresses || []).filter((_, i) => i !== idx) }));

    const addTag = (val) => {
        const t = val.trim();
        if (t && !(formData.tags || []).includes(t)) setFormData((prev) => ({ ...prev, tags: [...(prev.tags || []), t] }));
        setTagInput("");
    };
    const removeTag = (tag) => setFormData((prev) => ({ ...prev, tags: (prev.tags || []).filter((t) => t !== tag) }));

    const formatDateTime = (iso) => {
        if (!iso) return "";
        return new Date(iso).toLocaleString("en-IN", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    };

    const initials = (name = "") => name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "??";

    // Contact handlers
    const handleSaveContact = async () => {
        if (!newContact.name.trim()) { toast.warning("Contact name is required."); return; }
        if (!newContact.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newContact.email)) { toast.warning("Valid email required."); return; }
        setSavingContact(true);
        try {
            const isNew = editContactIdx === null;
            const url = isNew
                ? `${API_BASE_URL}/api/customers/${id}/contacts`
                : `${API_BASE_URL}/api/customers/${id}/contacts/${contacts[editContactIdx].id}`;
            const res = await fetch(url, {
                method: isNew ? "POST" : "PUT",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify(newContact),
            });
            if (!res.ok) throw new Error();
            toast.success(isNew ? "Contact added!" : "Contact updated!");
            setNewContact({ name: "", email: "", phone: "", designation: "", department: "", addresses: [], notes: "", primary: false });
            setEditContactIdx(null);
            fetchContacts();
        } catch { toast.error("Failed to save contact."); } finally { setSavingContact(false); }
    };

    const handleDeleteContact = (contactId, contactName) => {
        setConfirmModal({
            show: true,
            title: "Remove Contact",
            message: `Are you sure you want to remove "${contactName}"? This action cannot be undone.`,
            onConfirm: async () => {
                closeConfirm();
                try {
                    await fetch(`${API_BASE_URL}/api/customers/${id}/contacts/${contactId}`, {
                        method: "DELETE", headers: { Authorization: `Bearer ${token}` },
                    });
                    toast.success("Contact removed."); fetchContacts();
                } catch { toast.error("Failed to remove contact."); }
            },
        });
    };

    const handleSetPrimary = async (contactId) => {
        try {
            await fetch(`${API_BASE_URL}/api/customers/${id}/contacts/${contactId}/primary`, {
                method: "PUT", headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Primary contact updated."); fetchContacts();
        } catch { toast.error("Failed to update primary contact."); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.customerName?.trim()) { toast.warning("Customer name is required."); return; }
        if (!formData.customerType) { toast.warning("Customer type is required."); return; }
        try {
            const res = await fetch(`${API_BASE_URL}/api/customers/update/${id}`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            if (!res.ok) throw new Error(await res.text());
            toast.success("Customer updated successfully!");
            navigate(`/viewcustomer/${id}`);
        } catch (err) {
            toast.error(err.message || "Failed to update customer.");
        }
    };

    const openAddressModal = (idx = null) => {
        setEditAddressIdx(idx);
        setAddressDraft(idx !== null ? { ...(formData.addresses || [])[idx] } : emptyAddress());
        setAddressModal(true);
    };

    const saveAddressModal = () => {
        setFormData((prev) => {
            const updated = [...(prev.addresses || [])];
            if (editAddressIdx !== null) {
                updated[editAddressIdx] = addressDraft;
            } else {
                updated.push(addressDraft);
            }
            return { ...prev, addresses: updated };
        });
        setAddressModal(false);
    };

    return (
        <>
            <ConfirmModal
                show={confirmModal.show}
                title={confirmModal.title}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={closeConfirm}
            />
            <div className="fixed-top">
                <AdminNavbar />
            </div>
            <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>
                <div className="mb-3">
                    <h4 className="m-0">
                        <i className="bi bi-person-gear me-2"></i>
                        {formData.customerName ? formData.customerName : "Customer"}
                    </h4>
                    <div className="d-flex justify-content-between align-items-center mt-2">
                        <div>
                            <button type="submit" form="editCustomerForm" className="btn btn-primary me-2">Save</button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
                        </div>
                        <a href="/allcustomers" className="text-primary text-decoration-none me-3">List</a>
                    </div>
                </div>

                <form id="editCustomerForm" onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <Section title="Basic Information">
                        <div className="col-md-3">
                            <label className="form-label">Customer Code</label>
                            <input type="text" className="form-control" value={formData.customerCode || "Auto-generated"} readOnly
                                style={{ background: "#f0f0ee", fontFamily: "monospace", color: "#888" }} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Customer Name <span className="text-danger">*</span></label>
                            <input type="text" name="customerName" className="form-control" value={formData.customerName || ""} onChange={handleChange} required />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Customer Type <span className="text-danger">*</span></label>
                            <select name="customerType" className="form-select" value={formData.customerType || ""} onChange={handleChange}>
                                {CUSTOMER_TYPES.map((t) => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Industry</label>
                            <select name="industry" className="form-select" value={formData.industry || ""} onChange={handleChange}>
                                <option value="">Select industry…</option>
                                {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Website</label>
                            <input type="url" name="website" className="form-control" value={formData.website || ""} onChange={handleChange} placeholder="https://example.com" />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Account Manager</label>
                            <input type="text" name="accountManager" className="form-control" value={formData.accountManager || ""} onChange={handleChange} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Status <span className="text-danger">*</span></label>
                            <select name="status" className="form-select" value={formData.status || "Active"} onChange={handleChange}>
                                {STATUSES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                        </div>
                    </Section>
                </form>

                {/* Tabs */}
                <ul className="nav nav-tabs mb-2" style={{ backgroundColor: navbarColor }}>
                    {[
                        ["basic", "Address"],
                        ["contacts", "Contacts"],
                        ["billing", "Financial"],
                        ["notes", "Notes & Tags"],
                        ["system", "System Information"],
                    ].map(([tabId, label]) => (
                        <li className="nav-item" key={tabId}>
                            <button
                                type="button"
                                className={`nav-link ${activeTab === tabId ? "active" : ""}`}
                                onClick={() => setActiveTab(tabId)}
                                style={{
                                    color: activeTab === tabId ? navbarColor : "#fff",
                                    backgroundColor: activeTab === tabId ? "#fff" : "transparent",
                                    fontWeight: activeTab === tabId ? "600" : "normal",
                                }}
                            >
                                {label}
                                {tabId === "contacts" && contacts.length > 0 && (
                                    <span className="badge bg-secondary ms-1" style={{ fontSize: 10 }}>{contacts.length}</span>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>

                <div className="border p-3 mb-3">
                    {activeTab === "basic" && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold m-0">
                                    <i className="bi bi-geo-alt me-2"></i>Addresses ({(formData.addresses || []).length})
                                </h6>
                                <button type="button" className="btn btn-sm btn-primary" onClick={() => openAddressModal()}>
                                    <i className="bi bi-plus me-1"></i> Add Address
                                </button>
                            </div>

                            {(formData.addresses || []).length === 0 ? (
                                <p className="text-muted">No addresses added yet.</p>
                            ) : (
                                <table className="table table-sm table-hover align-middle">
                                    <thead>
                                        <tr>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Type</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Address</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>City</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>State</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Country</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Postal Code</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(formData.addresses || []).map((addr, idx) => (
                                            <tr key={idx}>
                                                <td><span className="badge bg-secondary">{addr.addressType || "Other"}</span></td>
                                                <td>{[addr.addressLine1, addr.addressLine2].filter(Boolean).join(", ") || "—"}</td>
                                                <td>{addr.city || "—"}</td>
                                                <td>{addr.state || "—"}</td>
                                                <td>{addr.country || "—"}</td>
                                                <td>{addr.postalCode || "—"}</td>
                                                <td>
                                                    <div className="d-flex gap-1">
                                                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openAddressModal(idx)}>
                                                            <i className="bi bi-pencil"></i>
                                                        </button>
                                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setConfirmModal({
                                                            show: true,
                                                            title: "Remove Address",
                                                            message: `Are you sure you want to remove this ${addr.addressType || "address"} address? This action cannot be undone.`,
                                                            onConfirm: () => { closeConfirm(); removeAddress(idx); },
                                                        })}>
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {activeTab === "contacts" && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold m-0">
                                    <i className="bi bi-people me-2"></i>Contacts ({contacts.length})
                                </h6>
                                <button type="button" className="btn btn-sm btn-primary" onClick={() => navigate(`/addcontact?customerId=${id}`)}>
                                    <i className="bi bi-plus me-1"></i> Add Contact
                                </button>
                            </div>
                            {contacts.length > 0 ? (
                                <table className="table table-sm table-hover align-middle">
                                    <thead>
                                        <tr>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Name</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Email</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Phone</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Designation</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Department</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Primary</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {contacts.map((c, i) => (
                                            <tr key={c.id || i}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div style={{ width: 30, height: 30, borderRadius: 6, background: "#eaf0f8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: "#3a6090", flexShrink: 0 }}>
                                                            {initials(c.name)}
                                                        </div>
                                                        <span className="fw-semibold">{c.name}</span>
                                                    </div>
                                                </td>
                                                <td>{c.email ? <a href={`mailto:${c.email}`} style={{ color: "#0052CC" }}>{c.email}</a> : "—"}</td>
                                                <td>{c.phone || "—"}</td>
                                                <td>{c.designation || "—"}</td>
                                                <td>{c.department || "—"}</td>
                                                <td>
                                                    {c.primary
                                                        ? <span className="badge" style={{ background: "#1d4ed8", color: "#fff" }}>★ Yes</span>
                                                        : <span className="text-muted">—</span>}
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-1">
                                                        {!c.primary && (
                                                            <button type="button" className="btn btn-sm btn-outline-success" title="Set as primary" onClick={() => handleSetPrimary(c.id)}>
                                                                <i className="bi bi-star"></i>
                                                            </button>
                                                        )}
                                                        <button type="button" className="btn btn-sm btn-outline-primary" title="Edit" onClick={() => navigate(`/editcontact/${c.id}`)}>
                                                            <i className="bi bi-pencil"></i>
                                                        </button>
                                                        <button type="button" className="btn btn-sm btn-outline-danger" title="Remove" onClick={() => handleDeleteContact(c.id, c.name)}>
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="text-muted">No contacts added yet.</p>
                            )}
                        </div>
                    )}

                    {activeTab === "billing" && (
                        <Section title="Financial">

                            <div className="col-md-3">
                                <label className="form-label">Currency <span className="text-danger">*</span></label>
                                <select name="currency" className="form-select" value={formData.currency || "INR"} onChange={handleChange}>
                                    {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Payment Terms</label>
                                <select name="paymentTerms" className="form-select" value={formData.paymentTerms || "Net 30"} onChange={handleChange}>
                                    {PAYMENT_TERMS.map((p) => <option key={p}>{p}</option>)}
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Tax Number / GST</label>
                                <input type="text" name="taxNumber" className="form-control" value={formData.taxNumber || ""} onChange={handleChange} />
                            </div>
                        </Section>
                    )}

                    {activeTab === "notes" && (
                        <>
                            <Section title="Tags">
                                <div className="col-md-12">
                                    <label className="form-label">Tags</label>
                                    <div className="border rounded p-2 d-flex flex-wrap gap-2 align-items-center" style={{ minHeight: 42, background: "#fafaf9" }}>
                                        {(formData.tags || []).map((t) => (
                                            <span key={t} className="badge" style={{ background: "#eaf0f8", color: "#3a6090", border: "1px solid #d0dff0", fontSize: 12 }}>
                                                {t}
                                                <button type="button" className="btn-close ms-1" style={{ fontSize: 8 }} onClick={() => removeTag(t)} />
                                            </span>
                                        ))}
                                        <input
                                            type="text" className="border-0 flex-grow-1"
                                            style={{ outline: "none", minWidth: 100, background: "transparent", fontSize: 13 }}
                                            placeholder="Add tag, press Enter…"
                                            value={tagInput}
                                            onChange={(e) => setTagInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagInput); }
                                                if (e.key === "Backspace" && !tagInput && formData.tags?.length) {
                                                    setFormData((p) => ({ ...p, tags: p.tags.slice(0, -1) }));
                                                }
                                            }}
                                            onBlur={() => tagInput.trim() && addTag(tagInput)}
                                        />
                                    </div>
                                    <div className="form-text">Press Enter or comma to add tags</div>
                                </div>
                            </Section>
                            <Section title="Internal Notes">
                                <div className="col-md-12">
                                    <label className="form-label">Notes</label>
                                    <textarea name="notes" className="form-control" rows={4} value={formData.notes || ""} onChange={handleChange} placeholder="Internal notes about this customer…" />
                                </div>
                            </Section>
                        </>
                    )}

                    {activeTab === "system" && (
                        <div className="p-3 border rounded bg-white">
                            <div className="mb-3">
                                <div className="form-check mb-2">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={formData.status === "Inactive"}
                                        onChange={(e) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                status: e.target.checked ? "Inactive" : "Active"
                                            }));
                                        }}
                                    />
                                    <label className="form-check-label fw-bold ms-2">INACTIVE</label>
                                </div>
                                <div className="text-muted small">
                                    <strong>Date Created:</strong> {formatDateTime(formData.createdAt)}
                                </div>
                            </div>

                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button className={`nav-link ${activeSubTab === "systemNotes" ? "active" : ""}`} onClick={() => setActiveSubTab("systemNotes")}>
                                        System Notes
                                    </button>
                                </li>
                            </ul>

                            {activeSubTab === "systemNotes" && (
                                <table className="table table-sm align-middle">
                                    <thead>
                                        <tr>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Date</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Set By</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Field</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>Old Value</th>
                                            <th style={{ backgroundColor: "#DFE4EB" }}>New Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {systemNotes.length === 0 ? (
                                            <tr><td colSpan="5" className="text-center text-muted">No records to show.</td></tr>
                                        ) : (
                                            systemNotes.map((note, i) => (
                                                <tr key={i}>
                                                    <td>{formatDateTime(note.modifiedDate)}</td>
                                                    <td>{note.modifiedBy}</td>
                                                    <td>{note.fieldName}</td>
                                                    <td>{note.oldValue || "—"}</td>
                                                    <td>{note.newValue || "—"}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>

                <div className="d-flex align-items-center mt-2 mb-4">
                    <button type="submit" form="editCustomerForm" className="btn btn-primary me-2">Save</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
                </div>
            </div>


            {/* Address Modal */}
            {addressModal && (
                <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: "#DFE4EB" }}>
                                <h5 className="modal-title fw-bold">
                                    <i className="bi bi-geo-alt me-2"></i>
                                    {editAddressIdx !== null ? "Edit Address" : "Add Address"}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setAddressModal(false)} />
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <label className="form-label">Address Type</label>
                                        <select className="form-select" value={addressDraft.addressType} onChange={(e) => setAddressDraft(p => ({ ...p, addressType: e.target.value }))}>
                                            {ADDRESS_TYPES.map((t) => <option key={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-9">
                                        <label className="form-label">Address Line 1</label>
                                        <input type="text" className="form-control" value={addressDraft.addressLine1} onChange={(e) => setAddressDraft(p => ({ ...p, addressLine1: e.target.value }))} placeholder="Street address" />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Address Line 2</label>
                                        <input type="text" className="form-control" value={addressDraft.addressLine2} onChange={(e) => setAddressDraft(p => ({ ...p, addressLine2: e.target.value }))} placeholder="Apartment, suite, building (optional)" />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">City</label>
                                        <input type="text" className="form-control" value={addressDraft.city} onChange={(e) => setAddressDraft(p => ({ ...p, city: e.target.value }))} />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">State / Province</label>
                                        <input type="text" className="form-control" value={addressDraft.state} onChange={(e) => setAddressDraft(p => ({ ...p, state: e.target.value }))} />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Country</label>
                                        <input type="text" className="form-control" value={addressDraft.country} onChange={(e) => setAddressDraft(p => ({ ...p, country: e.target.value }))} />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">Postal Code</label>
                                        <input type="text" className="form-control" value={addressDraft.postalCode} onChange={(e) => setAddressDraft(p => ({ ...p, postalCode: e.target.value }))} placeholder="PIN / ZIP" />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                 <button type="button" className="btn btn-primary" onClick={saveAddressModal}>
                                    {editAddressIdx !== null ? "Update Address" : "Add Address"}
                                </button>
                                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAddressModal(false)}>Cancel</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}